import { useMemo } from 'react';
import { keyBy, map } from 'lodash';
import { useAvailableItems } from 'hooks';
import { usePermissions } from 'modules/taboola-common-frontend-modules/authentication';
import { useReportPresetsWithState } from '../../../hooks/useReportPresetsWithState';
import { isCustomReportPreset } from '../../../utils';
import { GLOBAL_PRESET_ACCOUNT_ID } from '../constants';

export const useReportPresets = reportConfig => {
    const { id: reportId, presets: configPresets = {} } = reportConfig || {};
    const [storedReportPresets = {}] = useReportPresetsWithState(reportId);
    const isGlobalPresetsPermitted = usePermissions('ADS_CONSOLE_GLOBAL_PRESETS_VIEW');

    const presetsArray = useMemo(
        () =>
            Object.entries(configPresets).map(([presetName, presetConfig]) => ({
                presetName,
                ...presetConfig,
            })),
        [configPresets]
    );
    const filteredPresetsArray = useAvailableItems(presetsArray);

    const mergedReportPresets = useMemo(() => {
        const staticPresets = map(filteredPresetsArray, ({ presetName }) => ({ presetName }));
        const customPresets = isGlobalPresetsPermitted
            ? Object.entries(storedReportPresets)
                  .filter(([presetName]) => isCustomReportPreset(presetName))
                  .map(([presetName, { presetDisplayName, accountId }]) => ({
                      presetName,
                      presetDisplayName,
                      isGlobal: accountId === GLOBAL_PRESET_ACCOUNT_ID,
                      isCustom: true,
                  }))
            : [];

        const mergedPresetsList = staticPresets.concat(customPresets);
        const mergedPresetsMap = keyBy(mergedPresetsList, 'presetName');

        return mergedPresetsMap;
    }, [filteredPresetsArray, storedReportPresets, isGlobalPresetsPermitted]);

    return mergedReportPresets;
};
